import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, share, throwError } from 'rxjs';
import { SocketService } from 'src/app/services/app/socket-service/socket-service.service';
import { ToastService } from 'src/app/services/toast-service/toast.service';
import { Events } from 'src/app/utils/event-utils';
import { errorHandl } from 'src/app/utils/network-utils';
import { getAttachmentsError } from 'src/app/utils/support.utils';
import { sweetAlert } from 'src/app/utils/swal';
import { urls } from 'src/app/utils/url.utils';

@Injectable({
  providedIn: 'root'
})
export class SupportService {

  // shared, SocketService keeps one listener per event
  readonly chatMessage$ = this.socketService.addListener(Events.CHAT_MESSAGE).pipe(share());
  readonly chatRead$ = this.socketService.addListener(Events.CHAT_READ).pipe(share());
  readonly ticketCreated$ = this.socketService.addListener(Events.TICKET_CREATED).pipe(share());
  readonly ticketUpdated$ = this.socketService.addListener(Events.TICKET_UPDATED).pipe(share());

  constructor(
    private http: HttpClient,
    private socketService: SocketService,
    private toastService: ToastService,
    private router: Router
  ) { }

  // not tied to the caller, e.g. a dropdown closes as soon as the action is clicked
  // scope: the tenants picked when the reference has more than one, e.g. the vendor and member of a trip
  raiseTicket(referenceType: string, referenceId: string, scope?: any) {
    this.createTicket({ referenceType, referenceId, ...(scope && { scope }) }).subscribe((ticket: any) => {
      this.toastService.show('Ticket raised');
      this.router.navigate(['/support/tickets', ticket.id]);
    }, (err) => {
      sweetAlert('Error', err, 'error', 'OK');
    });
  }

  uploadAttachments(files: File[]) {
    const attachmentsError = getAttachmentsError(files);
    if (attachmentsError) {
      return throwError(() => attachmentsError);
    }
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    return this.post('/upload', formData);
  }

  searchReferences(queryParams: any) {
    return this.get('/references', queryParams);
  }

  getReferenceDetails(referenceType: string, referenceId: string) {
    return this.get(`/references/${referenceType}/${referenceId}`);
  }

  // tickets

  createTicket(payload: any) {
    return this.post('/tickets', payload);
  }

  getTickets(queryParams: any) {
    return this.get('/tickets', queryParams);
  }

  getTicket(ticketId: string) {
    return this.get(`/tickets/${ticketId}`);
  }

  updateTicket(ticketId: string, payload: any) {
    return this.http.patch(this.url(`/tickets/${ticketId}`), payload).pipe(catchError(errorHandl));
  }

  // chats

  getChats(queryParams: any) {
    return this.get('/chats', queryParams);
  }

  getChat(chatId: string) {
    return this.get(`/chats/${chatId}`);
  }

  openDirectChat(payload: any) {
    return this.post('/chats', payload);
  }

  getMessages(chatId: string, queryParams: any) {
    return this.get(`/chats/${chatId}/messages`, queryParams);
  }

  sendMessage(chatId: string, payload: any) {
    return this.post(`/chats/${chatId}/messages`, payload);
  }

  markChatAsRead(chatId: string) {
    return this.post(`/chats/${chatId}/read`, {});
  }

  private url(path: string) {
    return urls.API_URL + urls.SUPPORT + path;
  }

  private get(path: string, queryParams: any = {}) {
    const params = new HttpParams({ fromObject: queryParams });
    return this.http.get(this.url(path), { params }).pipe(catchError(errorHandl));
  }

  private post(path: string, body: any) {
    return this.http.post(this.url(path), body).pipe(catchError(errorHandl));
  }
}
